// Atoms — small reusable presentational components.

import React, { type CSSProperties, type ReactNode } from "react";

function _agoShort(tsMs: number): string {
  const secs = Math.floor((Date.now() - tsMs) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
}
import type { ContextPressure, SlotStatus } from "./types";

// ─── icons (small inline svg set) ────────────────────────────────────────

type IconProps = React.SVGProps<SVGSVGElement>;

export const Icon = {
  Chev: (p: IconProps) => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" {...p}>
      <path
        d="M3.5 1.5 L6.5 5 L3.5 8.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Check: (p: IconProps) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path
        d="M2.5 6.5 L5 9 L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  X: (p: IconProps) => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" {...p}>
      <path d="M2 2 L9 9 M9 2 L2 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Camera: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <rect x="1.5" y="3" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4.5 3 L5.5 1.8 H8.5 L9.5 3" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  ),
  Send: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <path
        d="M1.5 6.5 L11.5 1.5 L6.5 11.5 L5.5 7.5 Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Refresh: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <path
        d="M11 6.5 a4.5 4.5 0 1 1 -1.4 -3.2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M11 1.5 V3.7 H8.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
    </svg>
  ),
  Warn: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M7 1.5 L13 12 H1 Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M7 5.5 V8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="7" cy="10" r="0.7" fill="currentColor" />
    </svg>
  ),
  Heart: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path
        d="M7 12 C2 8.5 1.5 5 3.6 3.5 C5.2 2.4 6.4 3.4 7 4.4 C7.6 3.4 8.8 2.4 10.4 3.5 C12.5 5 12 8.5 7 12 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
    </svg>
  ),
  Bolt: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path
        d="M8 1 L3 8 H6.5 L5.5 13 L10.5 6 H7 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ),
  Pause: (p: IconProps) => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" {...p}>
      <rect x="2.5" y="2" width="2" height="7" fill="currentColor" />
      <rect x="6.5" y="2" width="2" height="7" fill="currentColor" />
    </svg>
  ),
  Play: (p: IconProps) => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" {...p}>
      <path d="M3 2 L9 5.5 L3 9 Z" fill="currentColor" />
    </svg>
  ),
  Gear: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <circle cx="7" cy="7" r="1.8" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path
        d="M7 1.5 V3 M7 11 V12.5 M1.5 7 H3 M11 7 H12.5 M3.1 3.1 L4.2 4.2 M9.8 9.8 L10.9 10.9 M3.1 10.9 L4.2 9.8 M9.8 4.2 L10.9 3.1"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  SignOut: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <path
        d="M6.5 1.5 H2.5 V11.5 H6.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M8 4 L11 6.5 L8 9"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M5.5 6.5 H11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
    </svg>
  ),
  Lock: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <rect x="2.5" y="6" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.3" fill="none" />
      <path d="M4.5 6 V4 a2 2 0 0 1 4 0 V6" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round" />
    </svg>
  ),
  Eye: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path
        d="M1 7 C2.5 4 4.6 2.5 7 2.5 S11.5 4 13 7 C11.5 10 9.4 11.5 7 11.5 S2.5 10 1 7 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
      <circle cx="7" cy="7" r="1.8" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  ),
  EyeOff: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M2 4 C4.5 7 9.5 7 12 4" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      <path
        d="M3 5 L1.6 6.6 M11 5 L12.4 6.6 M5.5 6 L4.7 8 M8.5 6 L9.3 8"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  ),
  // Stylised brain shape — two hemispheres + central fold. `filled` swaps
  // stroke-only for a filled silhouette, used to indicate "thinking on".
  Brain: (p: IconProps & { filled?: boolean }) => {
    const { filled, ...rest } = p;
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" {...rest}>
        <path
          d="M5 3.4 a2.2 2.2 0 0 0 -2.2 2.2 a1.8 1.8 0 0 0 -.6 3 a2.0 2.0 0 0 0 .4 2.7 a2.2 2.2 0 0 0 2.4 .6 V3.4 Z M11 3.4 a2.2 2.2 0 0 1 2.2 2.2 a1.8 1.8 0 0 1 .6 3 a2.0 2.0 0 0 1 -.4 2.7 a2.2 2.2 0 0 1 -2.4 .6 V3.4 Z"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinejoin="round"
          fill={filled ? "currentColor" : "none"}
        />
        <path
          d="M8 3 V13 M5.5 6.5 H 7 M8.8 6.5 H10.4 M5.7 9.5 H7 M9 9.5 H10.4"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
          fill="none"
          opacity={filled ? 0.55 : 1}
        />
      </svg>
    );
  },
};

// ─── brand mark ──────────────────────────────────────────────────────────

export function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden>
      <svg width="13" height="13" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.5" r="5" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none" />
        <circle cx="6.5" cy="1.7" r="1.4" fill="white" />
        <circle cx="11.0" cy="9.0" r="1.1" fill="rgba(255,255,255,0.85)" />
        <circle cx="2.2" cy="9.0" r="1.1" fill="rgba(255,255,255,0.7)" />
      </svg>
    </span>
  );
}

// ─── pill select (backend switcher) ──────────────────────────────────────

export interface PillOption {
  id: string;
  label: string;
  url: string;
  status?: "ok" | "stale" | "unknown";
  last_pinged_at?: number | null;
}

interface PillSelectProps {
  value: string;
  options: PillOption[];
  onChange: (id: string) => void;
  formatLabel?: (o: PillOption) => ReactNode;
  formatRow?: (o: PillOption) => ReactNode;
}

export function PillSelect({ value, options, onChange, formatLabel, formatRow }: PillSelectProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", click);
    return () => window.removeEventListener("mousedown", click);
  }, [open]);

  const current = options.find((o) => o.id === value) ?? options[0];
  if (!current) return null;

  return (
    <div className="pill-select" ref={ref}>
      <button className="pill-select-trigger" onClick={() => setOpen((o) => !o)}>
        {formatLabel ? (
          formatLabel(current)
        ) : (
          <>
            <span className={`status-dot ${current.status === "ok" ? "working" : "stale"}`} />
            <span className="label">{current.label}</span>
            <span className="url">{current.url.replace(/^https?:\/\//, "")}</span>
          </>
        )}
        <span className="chev">
          <Icon.Chev style={{ transform: open ? "rotate(90deg)" : "rotate(0)", transition: "120ms" }} />
        </span>
      </button>
      {open && (
        <div className="pill-select-menu" role="listbox">
          {options.map((o) => (
            <div
              key={o.id}
              className={`pill-select-item ${o.id === value ? "active" : ""}`}
              onClick={() => {
                onChange(o.id);
                setOpen(false);
              }}
              role="option"
              aria-selected={o.id === value}
            >
              <span className="check">
                <Icon.Check />
              </span>
              {formatRow ? (
                formatRow(o)
              ) : (
                <>
                  <span className={`status-dot ${o.status === "ok" ? "working" : "stale"}`} />
                  <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <span>{o.label}</span>
                    <span className="meta">{o.url}</span>
                    {o.status !== "ok" && (
                      <span className="meta" style={{ color: "var(--status-stale, #b91c1c)", fontSize: 11 }}>
                        {o.last_pinged_at ? `offline · last seen ${_agoShort(o.last_pinged_at)}` : "offline"}
                      </span>
                    )}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── status atoms ────────────────────────────────────────────────────────

export function StatusDot({ status, className = "" }: { status: SlotStatus; className?: string }) {
  return <span className={`status-dot ${status} ${className}`} />;
}

export function StatusBadge({ status }: { status: SlotStatus }) {
  return (
    <span className="status-cell">
      <StatusDot status={status} />
      <span className={`status-label ${status}`}>{status}</span>
    </span>
  );
}

// ─── context bar ─────────────────────────────────────────────────────────

interface CtxBarProps {
  pct: number;
  pressure: ContextPressure;
  compactionsLastHour?: number;
}

export function CtxBar({ pct, pressure, compactionsLastHour }: CtxBarProps) {
  const title =
    compactionsLastHour != null
      ? `${pct}% context (${pressure}) · ${compactionsLastHour} compaction${compactionsLastHour === 1 ? "" : "s"}/hr`
      : `${pct}% context used (${pressure})`;
  return (
    <div className="ctx-cell">
      <div className="ctx-bar" title={title}>
        <div className={`ctx-fill ${pressure}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className={`ctx-num ${pressure}`}>{pct}%</span>
      {compactionsLastHour != null && compactionsLastHour > 0 && (
        <span
          className={`mono ${pressure === "thrashing" ? "thrashing-tag" : ""}`}
          style={{ fontSize: 10.5, color: pressure === "thrashing" ? "var(--status-stale)" : "var(--fg-2)" }}
          title={`${compactionsLastHour} context compactions in the last hour`}
        >
          ⟳{compactionsLastHour}/hr
        </span>
      )}
    </div>
  );
}

// ─── toggle ──────────────────────────────────────────────────────────────

export function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button className={`toggle ${on ? "on" : ""}`} onClick={() => onChange(!on)} role="switch" aria-checked={on}>
      <span className="knob" />
    </button>
  );
}

// ─── live pill (top bar) ─────────────────────────────────────────────────

interface LivePillProps {
  connected: boolean;
  lastUpdated: string;
  serverStarted?: string | null;
  pollIntervalLabel?: string;
}

export function LivePill({ connected, lastUpdated, serverStarted, pollIntervalLabel = "60s" }: LivePillProps) {
  const title = !connected
    ? "disconnected"
    : serverStarted
      ? `polling /api/state every ${pollIntervalLabel} · server started ${new Date(serverStarted).toLocaleString()}`
      : `polling /api/state every ${pollIntervalLabel}`;
  return (
    <span className={`livepill ${connected ? "" : "off"}`} title={title}>
      <span className="dot" />
      <span>{connected ? "live" : "off"}</span>
      <span style={{ color: "var(--fg-3)" }}>·</span>
      <span style={{ color: "var(--fg-2)" }}>{lastUpdated}</span>
    </span>
  );
}

// ─── tabs (small segmented control) ──────────────────────────────────────

interface TabsProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}

export function Tabs<T extends string>({ value, onChange, options }: TabsProps<T>) {
  return (
    <div className="tabs" role="tablist">
      {options.map((o) => (
        <button key={o.value} className={`tab ${o.value === value ? "active" : ""}`} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── panel shell ─────────────────────────────────────────────────────────

interface PanelProps {
  title: string;
  count?: number | string;
  right?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  /** Role-based tone for the panel header. Each role resolves to a
   * CSS var (--tone-<role>-color) that the active palette
   * (body[data-palette]) restyles. So a panel says "I am agents" — not
   * "I am yellow" — and changing palette swaps colors site-wide. */
  tone?: "agents" | "fleet" | "backlog" | "blocked" | "prerequisites" | "activity" | "accounts";
}

export function Panel({ title, count, right, children, flush, tone }: PanelProps) {
  return (
    <div className={`panel${tone ? ` tone-${tone}` : ""}`}>
      <div className="panel-head">
        <span className="title">{title}</span>
        {typeof count !== "undefined" && (
          <span className="count">
            · <b>{count}</b>
          </span>
        )}
        <span className="spacer" />
        {right}
      </div>
      <div className={`panel-body ${flush ? "flush" : ""}`}>{children}</div>
    </div>
  );
}

// ─── empty state ─────────────────────────────────────────────────────────

interface EmptyProps {
  icon?: ReactNode;
  title: string;
  sub?: ReactNode;
}

export function Empty({ icon, title, sub }: EmptyProps) {
  return (
    <div className="empty-block">
      {icon && <div className="e-mark">{icon}</div>}
      <div style={{ color: "var(--fg-1)", fontWeight: 500, marginBottom: 2 }}>{title}</div>
      {sub && <div>{sub}</div>}
    </div>
  );
}

// ─── modal ───────────────────────────────────────────────────────────────

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title: string;
  danger?: boolean;
  style?: CSSProperties;
}

export function Modal({ open, onClose, children, title, danger }: ModalProps) {
  if (!open) return null;
  return (
    <div
      className="modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          {danger && <span className="badge">destructive</span>}
          <span>{title}</span>
        </div>
        {children}
      </div>
    </div>
  );
}
